import FederationConfig from "@/plugins/federationConfig";

import masterdata from "./routes/masterdata";
import backoffices from "./routes/backoffices";
import horses from "./routes/horses";
import events from "./routes/events";
import people from "./routes/people";
import organisations from "./routes/organisations";
import groupings from "./routes/groupings";

// Navigation
import LandingPage from "@/components/LandingPage";
import SignInForm from "@/components/SignInForm";
import AnonymouseBasket from "@/components/order/Orders";

// People
import PersonForm from "@/components/person/PersonForm";
import PersonRequestPasswordReset from "@/components/person/PersonRequestPasswordReset";

// Errors
import UnauthorizedAccess from "@/components/error_pages/403";
import LoginAccess from "@/components/error_pages/401";
import NoPage from "@/components/error_pages/404";

import MasterdataV2 from "./routes/masterdata2";

import PersonSelfSignup from "@/components/PersonSelfSignup.vue";

const mainRoutes = [
  {
    path: "/",
    name: "LandingPage",
    component: LandingPage,
    props: true,
    meta: { anchor: "#app", keepAlive: true }
  },
  {
    path: "/person_self_signup",
    name: "PersonSelfSignup",
    component: PersonSelfSignup,
    meta: { anchor: "#jumpBreadcrumb" }
  },
  {
    path: "/person_signup",
    name: "PersonSignupForm",
    component: PersonForm,
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/request_password_reset",
    name: "PersonRequestPasswordReset",
    component: PersonRequestPasswordReset,
    props: true,
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/join",
    name: "JoinFederation",
    component: PersonRequestPasswordReset,
    props: true,
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/ranking_tables",
    name: "RankingTablesPage",
    component: () =>
      import(
        /* webpackChunkName: "pho" */ "@/components/ranking/RankingTablesPage.vue"
      ),
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/history",
    name: "History",
    component: () =>
      import(/* webpackChunkName: "pho" */ "@/components/history/History.vue"),
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/login",
    name: "NormalLogin",
    component: LoginAccess,
    props: {
      normalLoginScreen: true
    },
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/sign_in",
    name: "SignIn",
    component: SignInForm,
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/shopping_basket/:shoppingBasketId",
    name: "AnonymouseBasket",
    props: true,
    component: AnonymouseBasket,
    meta: { breadcrumb: "Shopping basket", anchor: "#jumpMobile" }
  },
  {
    path: "/shop",
    name: "Shop",
    props: true,
    component: () =>
      import(/* webpackChunkName: "pho" */ "@/components/shop/Shop.vue"),
    meta: { breadcrumb: "Shop", anchor: "#jumpMobile" }
  },
  {
    path: "/competing_dfficulty_levels",
    name: "CompetingDifficultyLevelsPage",
    props: true,
    component: () =>
      import("@/components/competing_levels/CompetingDifficultyLevels.vue"),
    meta: { breadcrumb: "Levels", anchor: "#jumpMobile" }
  },
  // Errors
  {
    path: "/unauthorized_access",
    name: "403",
    component: UnauthorizedAccess,
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "/require_login",
    name: "401",
    component: LoginAccess,
    props: {
      returnPage: { name: null }
    },
    meta: { anchor: "#jumpMobile" }
  },
  {
    path: "*",
    name: "no page",
    component: NoPage,
    meta: { anchor: "#jumpMobile" }
  }
];

const supports = [
  {
    path: "/support",
    name: "Support",
    forFederation: ["FI"],
    meta: { keepAlive: true, anchor: "#jumpMobile" },
    component: () =>
      import(
        /* 
          webpackChunkName: "FinlandSupport",
          webpackPrefetch: true
        */

        "@/components/support_pages/FinlandSupport"
      )
  },
  {
    path: "/support",
    name: "Support",
    forFederation: ["NO"],
    meta: { keepAlive: true, anchor: "#jumpMobile" },
    component: () =>
      import(
        /* 
          webpackChunkName: "NorwaySupport",
          webpackPrefetch: true 
        */

        "@/components/support_pages/NorwaySupport"
      )
  },
  {
    path: "/support",
    name: "Support",
    forFederation: ["UA"],
    meta: { keepAlive: true, anchor: "#jumpMobile" },
    component: () =>
      import(
        /* 
          webpackChunkName: "UkraineSupport",
          webpackPrefetch: true  
        */
        "@/components/support_pages/UkraineSupport"
      )
  },
  {
    path: "/support",
    name: "Support",
    forFederation: ["BE"],
    meta: { keepAlive: true, anchor: "#jumpMobile" },
    component: () =>
      import(
        /* 
          webpackChunkName: "BelgiumSupport",
          webpackPrefetch: true  
        */
        "@/components/support_pages/BelgiumSupport"
      )
  },
  {
    path: "/support",
    name: "Support",
    forFederation: ["NL"],
    meta: { keepAlive: true, anchor: "#jumpMobile" },
    component: () =>
      import(
        /* 
          webpackChunkName: "NetherlandsSupport",
          webpackPrefetch: true  
        */
        "@/components/support_pages/NetherlandsSupport"
      )
  }
].filter(route => {
  if (!route.forFederation) return true;
  else return route.forFederation.includes(FederationConfig.countryCode);
});

let sandbox = [];
if (process.env.VUE_APP_ENV !== "production") {
  sandbox = [require("./routes/sandbox.js").default];
}

const allRoutes = [
  ...mainRoutes,
  ...supports,
  ...events,
  ...people,
  ...organisations,
  ...horses,
  ...masterdata,
  ...MasterdataV2,
  ...backoffices,
  ...groupings,
  ...sandbox,
  { path: "/404", alias: "*", name: "NoPage", component: NoPage }
].map(route => {
  if (route.forFederation) {
    if (route.forFederation.includes(FederationConfig.countryCode)) {
      return route;
    } else {
      return {
        path: route.path,
        name: route.name,
        redirect: { name: "NoPage", param: { customText: null } }
      };
    }
  } else {
    return route;
  }
});

export default allRoutes;
